import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import HeroSection from './components/HeroSection';
import TrustSection from './components/TrustSection';
import AirlineCarousel from './components/AirlineCarousel';
import DestinationsGrid from './components/DestinationsGrid';
import SpecialOffers from './components/SpecialOffers';
import UmrahGroupSection from './components/UmrahGroupSection';
import NewsletterSection from './components/NewsletterSection';
import AirlineNews from './components/AirlineNews';
import WhatsAppButton from './components/WhatsAppButton';

export default function FlightsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const schemaScripts: HTMLScriptElement[] = [];

    const travelAgencySchema = {
      '@context': 'https://schema.org',
      '@type': 'TravelAgency',
      name: 'Alwaz Travels',
      url: 'https://www.alwaz.travel/air-tickets-deals',
      logo: 'https://www.alwaz.travel/vite.svg',
      description: 'UK-based travel agency offering negotiated airfare deals worldwide, special family fares, Umrah group fares, tour packages, and long-haul discounted tickets.',
      telephone: '+447362505895',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'London',
        addressCountry: 'GB',
      },
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: 51.5074,
          longitude: -0.1278,
        },
      },
      priceRange: '$$',
      sameAs: [
        'https://wa.me/447362505895',
      ],
    };

    const offersSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Flight Deals This Week – Limited Time Offers',
      description: 'Exclusive negotiated fares available for a limited time only from Alwaz Travels London.',
      url: 'https://www.alwaz.travel/air-tickets-deals',
      numberOfItems: 4,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Offer',
            name: 'Emirates – London to Karachi via Dubai',
            description: 'Discounted Emirates flight from London to Karachi via Dubai. Save \u00A3180 on negotiated consolidator fares.',
            price: '459',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/LimitedAvailability',
            validFrom: '2025-01-15',
            validThrough: '2025-03-31',
            seller: {
              '@type': 'TravelAgency',
              name: 'Alwaz Travels',
            },
            offeredBy: {
              '@type': 'Airline',
              name: 'Emirates',
              iataCode: 'EK',
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Offer',
            name: 'Qatar Airways – London to Lahore via Doha',
            description: 'Discounted Qatar Airways flight from London to Lahore via Doha. Save \u00A3165 on negotiated consolidator fares.',
            price: '485',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/LimitedAvailability',
            validFrom: '2025-02-01',
            validThrough: '2025-04-30',
            seller: {
              '@type': 'TravelAgency',
              name: 'Alwaz Travels',
            },
            offeredBy: {
              '@type': 'Airline',
              name: 'Qatar Airways',
              iataCode: 'QR',
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Offer',
            name: 'Turkish Airlines – London to Islamabad via Istanbul',
            description: 'Discounted Turkish Airlines flight from London to Islamabad via Istanbul. Save \u00A3195 on negotiated consolidator fares.',
            price: '425',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/LimitedAvailability',
            validFrom: '2025-01-20',
            validThrough: '2025-03-15',
            seller: {
              '@type': 'TravelAgency',
              name: 'Alwaz Travels',
            },
            offeredBy: {
              '@type': 'Airline',
              name: 'Turkish Airlines',
              iataCode: 'TK',
            },
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Offer',
            name: 'Saudi Airlines – London to Jeddah Umrah Special',
            description: 'Discounted Saudi Airlines Umrah special flight from London to Jeddah for Ramadan 2025. Save \u00A3220 on negotiated fares.',
            price: '399',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/LimitedAvailability',
            validFrom: '2025-02-28',
            validThrough: '2025-03-30',
            seller: {
              '@type': 'TravelAgency',
              name: 'Alwaz Travels',
            },
            offeredBy: {
              '@type': 'Airline',
              name: 'Saudi Arabian Airlines',
              iataCode: 'SV',
            },
          },
        },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I book cheap flights from London to Pakistan?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Alwaz Travels offers negotiated consolidator fares from London to Pakistan via Emirates, Qatar Airways, Turkish Airlines, and PIA. Contact us on WhatsApp at +44 7362 505895 for the best available prices.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do you offer Umrah group flight deals from the UK?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, Alwaz Travels specialises in Umrah group flights from the UK with special Ramadan fares, family discounts, and custom group departures to Jeddah and Medina.',
          },
        },
        {
          '@type': 'Question',
          name: 'What airlines do you work with for discounted air tickets?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We work with major international airlines including British Airways, Emirates, Qatar Airways, Turkish Airlines, Etihad Airways, Saudi Arabian Airlines, PIA, Lufthansa, American Airlines, and Air India to offer the best negotiated fares.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get family flight discounts from London?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. Alwaz Travels offers special family discounts on flights from London to worldwide destinations including Pakistan, India, Saudi Arabia, and more. We negotiate group and family fares to ensure maximum savings.',
          },
        },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Discount Air Tickets from UK | Worldwide Flight Deals | Alwaz Travels',
      description: 'Book discounted worldwide air tickets with Alwaz Travels London. Special family fares, Umrah group deals, and negotiated airline prices. Get instant quotes on WhatsApp.',
      url: 'https://www.alwaz.travel/air-tickets-deals',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Alwaz Travels',
        url: 'https://www.alwaz.travel',
      },
      about: {
        '@type': 'Thing',
        name: 'Discounted Air Tickets and Flight Deals from UK',
      },
      mainEntity: {
        '@type': 'TravelAgency',
        name: 'Alwaz Travels',
      },
    };

    const schemas = [travelAgencySchema, offersSchema, faqSchema, webPageSchema];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      schemaScripts.push(script);
    });

    return () => {
      schemaScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <TrustSection />
      <AirlineCarousel />
      <DestinationsGrid />
      <SpecialOffers />
      <UmrahGroupSection />
      <NewsletterSection />
      <AirlineNews />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
